"use client";

import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Nav,
  Dropdown,
  Row,
  Col,
  Spinner,
  Form,
  Alert,
} from "react-bootstrap";
import {
  User,
  ArrowLeft,
  FileText,
  Star,
  Phone,
  Mail,
  ChevronDown,
  MoreHorizontal,
  Copy,
  Share2,
  Edit3,
  Settings,
  Globe2,
  AlertCircle,
  Users,
  X,
} from "lucide-react";

// Import your actual organization tabs here
import OrganizationSummaryTab from "../organizationpopup/organizationtabs/OrganizationSummaryTab.jsx";
import OrganizationDetailsTab from "../organizationpopup/organizationtabs/OrganizationDetailsTab.jsx";
import HRContactsTab from "../organizationpopup/organizationtabs/HRContactsTab.jsx"; // Import new HR Contacts tab
import CommentsSection from "../../commentSection/CommentSection.jsx"; // Import the reusable component

import { getContacts, getContactById } from "../../../../services/contactServices.js";
import { getPlacementOfficers, getPlacementOfficerById } from "../../../../services/PlacementOfficerServices.js";
import { getOrganizationById } from "../../../../services/organizationServices.js";

import "./OrganizationPopup.css";

const TabComponents = {
  summary: OrganizationSummaryTab,
  details: OrganizationDetailsTab,
  hrcontacts: HRContactsTab, // Add HR Contacts tab to components
};

const OrganizationPopupView = ({
  show,
  onHide,
  organization,
  onUpdateOrganization,
  defaultActiveTab = "summary",
}) => {
  const [activeTab, setActiveTab] = useState(defaultActiveTab); // Use prop for initial state
  const [rightPanelActiveTab, setRightPanelActiveTab] = useState("oneview");
  const [description, setDescription] = useState("");
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isStarred, setIsStarred] = useState(false);
  const [loadingTab, setLoadingTab] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [enrichedOrganization, setEnrichedOrganization] = useState(null);
  const [detailedOrganization, setDetailedOrganization] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);
  const fetchState = React.useRef({ contacts: false, officers: false, details: false });

  // Update activeTab when defaultActiveTab prop changes
  useEffect(() => {
    setActiveTab(defaultActiveTab);
  }, [defaultActiveTab, show]); // Also trigger when modal shows

  // Helper to parse IDs
  const parseIds = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) {
      return field.map(item => typeof item === 'object' ? item.id : String(item)).filter(Boolean);
    }
    if (typeof field === 'string') {
      return field.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  // Load contacts for contact name resolution by specific IDs
  const loadContacts = async () => {
    const baseOrg = detailedOrganization || organization;
    if (contacts.length > 0 || fetchState.current.contacts) return;
    fetchState.current.contacts = true;
    try {
      // Check if contactPersonIds already contains objects
      if (Array.isArray(baseOrg?.contactPersonIds) && baseOrg.contactPersonIds.length > 0 && typeof baseOrg.contactPersonIds[0] === 'object') {
        setContacts(baseOrg.contactPersonIds);
        return;
      }

      const contactIds = parseIds(baseOrg?.contactPersonIds);
      if (contactIds.length > 0) {
        const contactPromises = contactIds.map(id => getContactById(id).catch(() => null));
        const results = await Promise.all(contactPromises);
        const validContacts = results.map(r => r?.data || r).filter(Boolean);
        setContacts(validContacts);
      } else {
        setContacts([]);
      }
    } catch (error) {
      console.error("Failed to load contacts by ID:", error);
      fetchState.current.contacts = false;
    }
  };

  // Load officers for assigned user name resolution by specific IDs
  const loadOfficers = async () => {
    const baseOrg = detailedOrganization || organization;
    if (officers.length > 0 || fetchState.current.officers) return;
    fetchState.current.officers = true;
    try {
      // Check if assignedToIds already contains objects
      if (Array.isArray(baseOrg?.assignedToIds) && baseOrg.assignedToIds.length > 0 && typeof baseOrg.assignedToIds[0] === 'object') {
        setOfficers(baseOrg.assignedToIds);
        return;
      }

      const officerIds = parseIds(baseOrg?.assignedToIds);
      if (officerIds.length > 0) {
        const officerPromises = officerIds.map(id => getPlacementOfficerById(id).catch(() => null));
        const results = await Promise.all(officerPromises);
        const validOfficers = results.map(r => r?.data || r).filter(Boolean);
        setOfficers(validOfficers);
      } else {
        setOfficers([]);
      }
    } catch (error) {
      console.error("Failed to load officers by ID:", error);
      fetchState.current.officers = false;
    }
  };

  // Load full organization details if we only have a stub
  const loadFullOrganization = async () => {
    if (!organization?.id || fetchState.current.details) return;
    
    // Only fetch if we're missing key fields
    if (organization.status && organization.contactPersonIds) return;

    fetchState.current.details = true;
    setLoadingDetails(true);
    try {
      const response = await getOrganizationById(organization.id);
      const data = response?.data?.[0] || response?.data || response;
      if (data) {
        setDetailedOrganization(data);
      }
    } catch (error) {
      console.error("Failed to load organization details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Enrich organization data with contact and user names
  useEffect(() => {
    const baseOrg = detailedOrganization || organization;
    if (baseOrg && (contacts.length > 0 || officers.length > 0)) {
      const contactPersonId = Array.isArray(baseOrg.contactPersonIds)
        ? (typeof baseOrg.contactPersonIds[0] === 'object' ? baseOrg.contactPersonIds[0].id : baseOrg.contactPersonIds[0])
        : baseOrg.contactPersonIds;
        
      const assignedToId = Array.isArray(baseOrg.assignedToIds)
        ? (typeof baseOrg.assignedToIds[0] === 'object' ? baseOrg.assignedToIds[0].id : baseOrg.assignedToIds[0])
        : baseOrg.assignedToIds;

      const contactPerson = contacts.find(
        (contact) => contact.id === contactPersonId
      );
      const assignedOfficer = officers.find(
        (o) => o.id === assignedToId
      );

      setEnrichedOrganization({
        ...baseOrg,
        contactPersonName: contactPerson?.name || "Unknown",
        email: baseOrg.email || contactPerson?.email || "",
        phone: baseOrg.phone || contactPerson?.phone || "",
        assignedToName: assignedOfficer
          ? `${assignedOfficer.firstName} ${assignedOfficer.lastName}`.trim()
          : "Unknown",
        assignedToEmail: assignedOfficer?.emailId || "",
        originalContactPersonId: baseOrg.contactPersonIds,
        originalAssignedToId: baseOrg.assignedToIds,
      });
    } else if (baseOrg) {
      setEnrichedOrganization({
        ...baseOrg,
        contactPersonName: "Unknown",
        assignedToName: "Unknown",
        email: baseOrg.email || "",
        phone: baseOrg.phone || "",
        assignedToEmail: "",
        originalContactPersonId: baseOrg.contactPersonIds,
        originalAssignedToId: baseOrg.assignedToIds,
      });
    }
  }, [organization, detailedOrganization, contacts, officers]);

  const lastOrgId = React.useRef(null);

  useEffect(() => {
    if (show && organization) {
      // Reset local state if opening a different organization
      if (lastOrgId.current !== organization.id) {
        setContacts([]);
        setOfficers([]);
        setDetailedOrganization(null);
        fetchState.current = { contacts: false, officers: false, details: false };
        lastOrgId.current = organization.id;
      }
      
      const baseOrg = detailedOrganization || organization;
      if (!fetchState.current.details) loadFullOrganization();
      if (contacts.length === 0 && !fetchState.current.contacts) loadContacts();
      if (officers.length === 0 && !fetchState.current.officers) loadOfficers();
    }
  }, [show, organization, contacts, officers, detailedOrganization]);

  // Handle comments error
  const handleCommentsError = (error) => {
    setError(error);
  };

  if (!organization) return null;

  const tabs = [
    { key: "summary", label: "Summary", icon: User },
    { key: "details", label: "Details", icon: FileText },
    { key: "hrcontacts", label: "HR Contacts", icon: Users },
  ];

  const visibleTabs = tabs.slice(0, 4);
  const hiddenTabs = tabs.slice(4);
  const TabIcon = ({ icon: Icon, active }) => (
    <Icon
      size={16}
      color={active ? "#ea580c" : "#6c757d"}
      style={{ marginRight: "6px" }}
    />
  );

  const handleCopyOrganization = () =>
    navigator.clipboard.writeText(`${organization.name}`);
  const handleShareOrganization = () => {};
  const handleEditOrganization = () => {};
  const handleEmail = () =>
    (window.location.href = `mailto:${enrichedOrganization?.email}`);
  const handleCall = () =>
    (window.location.href = `tel:${enrichedOrganization?.phone}`);
  const handleWebsite = () =>
    window.open(
      organization.website?.startsWith("http")
        ? organization.website
        : `https://${organization.website}`,
      "_blank"
    );

  const handleTabSelect = (key) => {
    setLoadingTab(true);
    setTimeout(() => {
      setActiveTab(key);
      setLoadingTab(false);
    }, 500);
  };

  const CurrentTabComponent = TabComponents[activeTab];
  const renderTabContent = () => {
    if (loadingTab) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "300px",
          }}
        >
          <Spinner
            animation="border"
            style={{
              color: "#f97316",
              width: "2.5rem",
              height: "2.5rem",
              borderWidth: "3px",
            }}
          />
          <p style={{ marginTop: "16px", color: "#6c757d" }}>Loading...</p>
        </div>
      );
    }
    return (
      <CurrentTabComponent
        organization={enrichedOrganization || organization}
        onHide={onHide}
        onUpdateOrganization={onUpdateOrganization}
        description={description}
        setDescription={setDescription}
        isEditingDescription={isEditingDescription}
        setIsEditingDescription={setIsEditingDescription}
        contacts={contacts}
        users={officers}
      />
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="organization-modal"
      backdrop="static"
    >
      <div className="modal-container" style={{ padding: "20px" }}>
        <Modal.Header className="organization-modal-header" style={{ position: "relative" }}>
          <Button
            variant="link"
            onClick={onHide}
            style={{
              position: "absolute",
              top: "10px",
              right: "15px",
              zIndex: 1060,
              color: "#6c757d",
              textDecoration: "none",
              border: "none",
              background: "transparent",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              transition: "all 0.2s ease",
            }}
            className="modal-close-x-btn"
            title="Close"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f8f9fa";
              e.currentTarget.style.color = "#212529";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#6c757d";
            }}
          >
            <X size={20} />
          </Button>
          <div className="header-content" style={{ paddingRight: "40px" }}>
            <div className="left-section">
              <Button variant="link" onClick={onHide} className="close-btn">
                <ArrowLeft size={20} />
              </Button>
              <div className="organization-info">
                <div className="avatar">
                  {organization.name?.charAt(0) || "O"}
                </div>
                <div className="organization-details">
                  <h5 className="org-name">{organization.name}</h5>
                  <div className="name-icons">
                    {/* <Button variant="link" className="name-icon-btn" onClick={() => setIsStarred(!isStarred)}>
                      <Star size={16} color={isStarred ? "#ffc107" : "#6c757d"} fill={isStarred ? "#ffc107" : "none"} />
                    </Button>
                    <Button variant="link" className="name-icon-btn" onClick={handleCopyOrganization}>
                      <Copy size={16} />
                    </Button>
                    <Button variant="link" className="name-icon-btn" onClick={handleShareOrganization}>
                      <Share2 size={16} />
                    </Button> */}
                  </div>
                </div>
              </div>
            </div>
            <div className="right-section">
              {/* <Button
                variant="outline-warning"
                size="sm"
                className="convert-btn"
              >
                Convert Organization
              </Button> */}
              <div className="lead-status-container">
                {/* <span className="lead-label">Status</span>
                <div className="lead-dropdown">
                  <Form.Select
                    size="sm"
                    className="lead-select"
                    defaultValue={organization.status || "prospect"}
                  >
                    <option value="prospect">Prospect</option>
                    <option value="client">Client</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                  <ChevronDown size={12} className="dropdown-icon" />
                </div> */}
              </div>
              <div className="action-buttons">
                <Button
                  variant="link"
                  className="name-icon-btn"
                  onClick={() => handleTabSelect("details")}
                >
                  <Edit3 size={16} />
                </Button>
                {/* <Button
                  variant="link"
                  size="sm"
                  className="action-btn"
                  onClick={handleEmail}
                >
                  <Mail size={16} />
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="action-btn"
                  onClick={handleCall}
                >
                  <Phone size={16} />
                </Button> */}
                <Button
                  variant="link"
                  size="sm"
                  className="action-btn"
                  onClick={handleWebsite}
                >
                  <Globe2 size={16} />
                </Button>
                {/* <Button variant="link" size="sm" className="action-btn">
                  <MoreHorizontal size={16} />
                </Button> */}
              </div>
            </div>
          </div>
        </Modal.Header>

        {/* Navigation Tabs */}
        <div className="tabs-container">
          <Nav variant="tabs" className="custom-tabs">
            {visibleTabs.map((tab) => (
              <Nav.Item key={tab.key}>
                <Nav.Link
                  active={activeTab === tab.key}
                  onClick={() => handleTabSelect(tab.key)}
                  className="custom-tab-link"
                >
                  <TabIcon icon={tab.icon} active={activeTab === tab.key} />
                  {tab.label}
                </Nav.Link>
              </Nav.Item>
            ))}
            {hiddenTabs.length > 0 && (
              <Nav.Item className="more-tabs-item">
                <Dropdown>
                  <Dropdown.Toggle as={Nav.Link} className="more-tabs-link">
                    <span className="tab-count">{hiddenTabs.length} More</span>
                    <ChevronDown size={12} className="more-dropdown-icon" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {hiddenTabs.map((tab) => (
                      <Dropdown.Item
                        key={tab.key}
                        onClick={() => handleTabSelect(tab.key)}
                        className={activeTab === tab.key ? "active" : ""}
                      >
                        <TabIcon
                          icon={tab.icon}
                          active={activeTab === tab.key}
                        />
                        {tab.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Nav.Item>
            )}
            {/* <Nav.Item className="settings-tab-item">
              <Nav.Link className="settings-tab-link">
                <Settings size={16} />
              </Nav.Link>
            </Nav.Item> */}
          </Nav>
        </div>

        <Modal.Body className="organization-modal-body">
          {/* Error Alert */}
          {error && (
            <Alert
              variant="danger"
              className="mb-4"
              dismissible
              onClose={() => setError(null)}
            >
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}

          {/* Summary Tab: Two-column layout with right panel */}
          {activeTab === "summary" && (
            <Row className="main-content">
              <Col lg={12} className="left-panel">
                {renderTabContent()}

                {/* Organization Comments Section */}
                {organization.id && (
                  <CommentsSection
                    entityId={organization.id} // Pass the specific organization ID as commentOnId
                    type="orgnization" // Specify this is for organizations
                    title={`Discussion about ${organization.name}`}
                    users={officers}
                    onError={handleCommentsError}
                    showCollapse={true}
                    defaultExpanded={true}
                    maxHeight="500px"
                    className="organization-comments-section"
                  />
                )}
              </Col>
            </Row>
          )}

          {/* All other tabs: Full width, no right panel */}
          {activeTab !== "summary" && (
            <div className="full-width-content">{renderTabContent()}</div>
          )}
        </Modal.Body>
      </div>

      <style>{`
        .organization-modal-body {
          padding: 0;
        }

        .main-content {
          margin: 0;
        }

        .left-panel {
          padding: 20px;
          background: white;
        }

        .full-width-content {
          padding: 20px;
          width: 100%;
        }

        .organization-modal-header {
          border-bottom: 1px solid #e9ecef;
          padding: 16px 20px;
          background: linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        }

        .left-section {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .close-btn {
          padding: 8px;
          color: #6c757d;
          text-decoration: none;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: #f8f9fa;
          color: #495057;
        }

        .organization-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #ea580c 0%, #0056b3 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 1.2rem;
          text-transform: uppercase;
        }

        .organization-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .org-name {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #212529;
        }

        .name-icons {
          display: flex;
          gap: 4px;
        }

        .name-icon-btn {
          padding: 4px;
          color: #6c757d;
          text-decoration: none;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .name-icon-btn:hover {
          background: #f8f9fa;
          color: #495057;
        }

        .right-section {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .convert-btn {
          border-color: #ffc107;
          color: #ffc107;
          font-weight: 500;
          font-size: 0.875rem;
          padding: 6px 16px;
        }

        .convert-btn:hover {
          background: #ffc107;
          border-color: #ffc107;
          color: #212529;
        }

        .lead-status-container {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .lead-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #6c757d;
        }

        .lead-dropdown {
          position: relative;
        }

        .lead-select {
          border: 1px solid #dee2e6;
          border-radius: 6px;
          padding-right: 30px;
          font-size: 0.875rem;
          min-width: 120px;
        }

        .dropdown-icon {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          color: #6c757d;
        }

        .action-buttons {
          display: flex;
          gap: 4px;
        }

        .action-btn {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6c757d;
          text-decoration: none;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .action-btn:hover {
          background: #f8f9fa;
          color: #495057;
        }

        .tabs-container {
          border-bottom: 1px solid #e9ecef;
          background: white;
        }

        .custom-tabs {
          border-bottom: none;
          padding: 0 20px;
        }

        .custom-tab-link {
          color: #6c757d;
          border: none;
          padding: 12px 16px;
          font-weight: 500;
          border-radius: 0;
          transition: all 0.2s ease;
        }

        .custom-tab-link:hover {
          color: #495057;
          background: #f8f9fa;
          border-color: transparent;
        }

        .custom-tab-link.active {
          color: #ea580c;
          background: white;
          border-bottom: 2px solid #ea580c;
        }

        .more-tabs-item .more-tabs-link {
          color: #6c757d;
          padding: 12px 16px;
          font-weight: 500;
          text-decoration: none;
        }

        .more-tabs-link:hover {
          color: #495057;
          background: #f8f9fa;
        }

        .tab-count {
          font-size: 0.875rem;
        }

        .more-dropdown-icon {
          margin-left: 4px;
        }

        .settings-tab-item .settings-tab-link {
          color: #6c757d;
          padding: 12px 16px;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .settings-tab-link:hover {
          color: #495057;
          background: #f8f9fa;
        }

        /* Comments Section Specific Styling for Organizations */
        .organization-comments-section {
          border: 2px solid #cce7ff;
          background: linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%);
          margin-top: 30px;
        }

        .debug-info {
          border-left: 3px solid #ea580c;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 16px;
            align-items: flex-start;
          }

          .right-section {
            align-self: flex-end;
          }

          .custom-tabs {
            padding: 0 10px;
          }

          .left-panel {
            padding: 15px;
          }
        }

        /* Additional Organization-specific styles */
        .organization-modal .modal-dialog {
          max-width: 90vw;
        }

        .organization-info .avatar {
          box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
        }

        .lead-status-container .lead-select:focus {
          border-color: #ea580c;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        .action-btn:focus {
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      `}</style>
    </Modal>
  );
};

export default OrganizationPopupView;
